"use client"

import React, { useMemo } from "react"
import { usePathname, useRouter } from "next/navigation"
import * as Icons from "lucide-react"
import { ExternalLink, type LucideIcon } from "lucide-react"

import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
} from "@/components/ui/sidebar"

import { useAuth } from "@/contexts/AuthContext"
import { useMenu } from "@/hooks/useMenu"
import { useFondoFeder } from "@/contexts/FondoFederContext"
import { applyUserTranslationsAllTypes } from "@/lib/utils"
import { apiUrl } from "@/lib/config"
import type { Module, Page } from "@/types/models/menu"
import type { FederPAI, Translation } from "@/types/types"
import { AppSidebarSubPages } from "@/components/core/AppSidebarSubPages"
import ModuleSwitcherDialog from "@/components/core/ModuleSwitcherDialog"
import { useTenantModules } from "@/hooks/useTenantModules"

const activeClass = "bg-sidebar-accent text-sidebar-accent-foreground"
const hoverClass = "hover:bg-sidebar-accent/60"
const federBase = "/proyectos-financiados/feder-pai"

const normalizePath = (path: string) => path.replace(/\/+/g, "/").replace(/\/$/, "")

const AppSidebar: React.FC = () => {
  const pathname = usePathname()
  const router = useRouter()
  const { currentTenantId, userLang, currentTenant, isLoading: authLoading } = useAuth()
  const { federPais: fondos } = useFondoFeder()
  const { menu, isLoading: menuLoading } = useMenu({
    tenantId: currentTenantId,
    enabled: !authLoading && Boolean(currentTenantId),
  })
  const { modules: tenantModules, isLoading: modulesLoading } = useTenantModules(currentTenantId, !authLoading && Boolean(currentTenantId))

  const isDipu = Boolean(currentTenant?.es_dipu)
  const normalizedPathname = normalizePath(pathname)
  const pathParts = normalizedPathname.split("/").filter(Boolean)
  const federIndex = pathParts.indexOf("feder-pai")

  const numericPaiId =
    federIndex !== -1 && pathParts[federIndex + 1] && !Number.isNaN(Number(pathParts[federIndex + 1]))
      ? pathParts[federIndex + 1] : undefined

  const isInFederDetail = Boolean(numericPaiId)

  const federCode = useMemo(() => {
    if (!numericPaiId || !fondos?.length) return null
    return fondos.find((item: FederPAI) => String(item?.id) === numericPaiId)?.codigo ?? null
  }, [fondos, numericPaiId])

  const isActive = (url: string) => {
    const normalizedUrl = normalizePath(url)
    return normalizedPathname === normalizedUrl || normalizedPathname.startsWith(`${normalizedUrl}/`)
  }

  const getTranslatedText = (item: { text: string; translations: Translation[] }) =>
    applyUserTranslationsAllTypes([item], userLang, ["text"])[0].text

  const renderPage = (page: Page) => {
    const Icon = Icons[page.icon as keyof typeof Icons] as LucideIcon | undefined
    const isFederPage = page.slug.includes(federBase)

    const pageTitle =
      isFederPage && federCode && isInFederDetail
        ? `${getTranslatedText(page)} - ${federCode}`
        : getTranslatedText(page)

    return (
      <SidebarMenuItem key={page.slug}>
        <SidebarMenuButton
          onClick={() => router.push(page.slug)}
          className={isActive(page.slug) ? activeClass : hoverClass}
        >
          <div className="flex items-center gap-3">
            {Icon && <Icon className="h-4 w-4 shrink-0" />}
            <span>{pageTitle}</span>
          </div>
        </SidebarMenuButton>

        {!!page.subpages?.length && (
          <AppSidebarSubPages
            page={page}
            subpages={page.subpages}
            isDipu={isDipu}
            isInFederDetail={isInFederDetail}
            normalizedPathname={normalizedPathname}
            numericPaiId={numericPaiId}
            userLang={userLang}
          />
        )}
      </SidebarMenuItem>
    )
  }

  const renderModule = (module: Module) => (
    <SidebarGroup key={module.text}>
      <SidebarGroupLabel className="text-sidebar-foreground/70 uppercase text-xs font-semibold tracking-wider">
        {getTranslatedText(module)}
      </SidebarGroupLabel>

      <SidebarGroupContent>
        <SidebarMenu>{module.page.map(renderPage)}</SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )

  if (authLoading || menuLoading || modulesLoading) return null

  return (
    <Sidebar className="border-r border-sidebar-border bg-sidebar-background text-sidebar-foreground flex flex-col">
      <SidebarHeader className="border-b border-sidebar-border bg-sidebar-background p-4">
        <ModuleSwitcherDialog
          currentModule="core"
          modules={tenantModules}
          trigger={
            <button type="button" className="flex h-10 w-full items-center justify-center rounded-md transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-primary" aria-label="Cambiar módulo">
              {menu?.image && (
                <img src={`${apiUrl}/assets/${menu.image}`} alt="Urban Studio" className="h-10 w-auto dark:hidden" />
              )}
              {menu?.image_dark && (
                <img src={`${apiUrl}/assets/${menu.image_dark}`} alt="Urban Studio" className="hidden h-10 w-auto dark:block" />
              )}
              {!menu?.image && !menu?.image_dark && <Icons.LayoutGrid className="h-8 w-8 text-sidebar-primary" />}
            </button>
          }
        />
      </SidebarHeader>

      <SidebarContent className="flex-1 gap-0 bg-sidebar-background">
        {menu?.module.map(renderModule)}
        <SidebarGroup className="mt-auto">
          <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/70">
            Aplicaciones
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <ModuleSwitcherDialog
                  currentModule="core"
                  modules={tenantModules}
                  trigger={
                    <SidebarMenuButton className={hoverClass} aria-label="Cambiar módulo">
                      <Icons.PanelsTopLeft className="h-4 w-4" />
                      <span>Cambiar módulo</span>
                    </SidebarMenuButton>
                  }
                />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border bg-sidebar-background p-4">
        <div className="text-center">
          <p className="mb-1 text-xs text-sidebar-foreground/60">{menu?.footer}</p>

          <a
            href={menu?.link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 text-sm font-medium text-sidebar-primary hover:text-sidebar-primary/80"
          >
            TECH friendly
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </SidebarFooter>
    </Sidebar>
  )
}

export default AppSidebar
